import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Text,
  Button,
  majorScale,
  Spinner,
  Alert,
  Strong,
} from 'evergreen-ui';
import { ProgressBar } from './common';
import { useCsvImport } from './hooks/useCsvImport';

const ImportProgress = ({ importSession, onComplete, onError }) => {
  const [progress, setProgress] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { importCsvData, getImportProgress, cancelImport } = useCsvImport();

  useEffect(() => {
    let pollInterval;

    const startImport = async () => {
      try {
        setIsImporting(true);
        await importCsvData(importSession.id);
        pollProgress();
      } catch (error) {
        onError(error.message);
        setIsImporting(false);
      }
    };

    const pollProgress = async () => {
      pollInterval = setInterval(async () => {
        try {
          const progressData = await getImportProgress(importSession.id);
          setProgress(progressData);

          if (progressData.status === 'completed') {
            clearInterval(pollInterval);
            setIsImporting(false);
            onComplete();
          } else if (progressData.status === 'failed') {
            clearInterval(pollInterval);
            setIsImporting(false);
            onError(progressData.error || 'Import failed');
          }
        } catch (error) {
          clearInterval(pollInterval);
          setIsImporting(false);
          onError(error.message);
        }
      }, 1000);
    };

    startImport();

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [importSession.id]);

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      await cancelImport(importSession.id);
      onError('Import cancelled by user');
    } catch (error) {
      onError(error.message);
    } finally {
      setIsCancelling(false);
      setIsImporting(false);
    }
  };

  if (!progress) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
        <Spinner />
      </Pane>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'cancelled':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Pane>
      <Card
        backgroundColor="white"
        elevation={0}
        border="default"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
      >
        <Pane marginBottom={majorScale(2)}>
          <Alert
            intent={getStatusColor(progress.status)}
            title={`Import ${progress.status}`}
            marginBottom={majorScale(2)}
          >
            {progress.message}
          </Alert>
        </Pane>

        <Pane marginBottom={majorScale(3)}>
          <Text>
            <Strong>Progress: </Strong>
            {Math.round(progress.progress_percentage)}%
          </Text>
          <ProgressBar
            value={progress.progress_percentage}
            marginTop={majorScale(1)}
          />
        </Pane>

        <Pane>
          <Text>
            <Strong>Processed: </Strong>
            {progress.processed_rows} of {progress.total_rows} rows
          </Text>
        </Pane>
      </Card>

      {isImporting && (
        <Pane display="flex" justifyContent="flex-end">
          <Button
            intent="danger"
            onClick={handleCancel}
            disabled={isCancelling}
            marginRight={majorScale(2)}
          >
            {isCancelling ? <Spinner size={16} /> : 'Cancel Import'}
          </Button>
        </Pane>
      )}
    </Pane>
  );
};

export default ImportProgress;
